const ignoredExceptions = [
	'ErrorEvent: ResizeObserver loop limit exceeded',
	'ErrorEvent: ResizeObserver loop completed with undelivered notifications.',
	'PromiseRejectionEvent: AbortError: The user aborted a request.',
];

interface BootInfo {
	currentUser: { id: string };
}

declare global {
	interface DocumentEventMap {
		boot: CustomEvent<BootInfo>;
		'cz-version-loaded': CustomEvent<{ versionNumber: string }>;
	}
}
import type { BrowserOptions } from '@sentry/browser';

type AppInsightsInstance = {
	trackException: (e: {
		exception: Error;
		properties?: Record<string, string>;
	}) => void;
};
type SentryInstance = {
	captureException: (
		error: Error,
		context?: { tags?: Record<string, string> },
	) => void;
};

let _appInsights: AppInsightsInstance | null = null;
let _sentry: SentryInstance | null = null;

export const captureException = (
	error: Error,
	context?: Record<string, string>,
) => {
	_appInsights?.trackException?.({ exception: error, properties: context });
	_sentry?.captureException?.(error, { tags: context });
};

export const initAppInsights = async (azureConnectionString: string) => {
	try {
		const { ApplicationInsights } =
			await import('@neovici/applicationinsights-web');
		const appInsights = new ApplicationInsights({
			config: {
				connectionString: azureConnectionString,
				enableUnhandledPromiseRejectionTracking: true,
			},
		});
		appInsights.loadAppInsights();
		appInsights.trackPageView();
		appInsights.addTelemetryInitializer((envelope) => {
			if (ignoredExceptions.includes(envelope.data?.message)) {
				return false;
			}
		});
		document.addEventListener('boot', ({ detail }) => {
			const validatedId = detail.currentUser.id.replace(/[,;=| ]+/gu, '_');
			appInsights.setAuthenticatedUserContext(validatedId, undefined, true);
		});
		_appInsights = appInsights;
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error('Failed to load application insights!');
		// eslint-disable-next-line no-console
		console.error(e);
	}
};
type Integrations = Extract<BrowserOptions['integrations'], Array<unknown>>;

const defaultTargets = [
	'https://alpha.cosmoz.com',
	'https://beta.cosmoz.com',
	/^\/api\//u,
];

export const initSentry = async (config: {
	dsn: string;
	environment?: string;
	versionNumber?: string;
	tracing?:
		| boolean
		| { sampleRate?: number; propagationTargets?: (string | RegExp)[] };
	profiling?: boolean | { sampleRate?: number };
	replays?:
		| boolean
		| { sampleRate?: number; errorSampleRate?: number; maskAllText?: boolean };
}) => {
	try {
		const Sentry = await import('@sentry/browser');
		const {
			dsn,
			environment = 'development',
			versionNumber,
			tracing: _tracing,
			profiling: _profiling,
			replays: _replays,
		} = config;
		const integrations: Integrations = [];
		const options: Omit<BrowserOptions, 'integrations'> & {
			integrations: Integrations;
		} = {
			dsn,
			environment,
			release: versionNumber,
			integrations,
		};
		if (_tracing) {
			const tracing = typeof _tracing == 'boolean' ? {} : _tracing;
			integrations.push(Sentry.browserTracingIntegration());
			options.tracesSampleRate = tracing.sampleRate ?? 0.5;
			options.tracePropagationTargets =
				tracing.propagationTargets ?? defaultTargets;
		}
		if (_profiling) {
			const profiling = typeof _profiling == 'boolean' ? {} : _profiling;
			integrations.push(Sentry.browserProfilingIntegration());
			options.profilesSampleRate = profiling.sampleRate ?? 0.5;
		}
		if (_replays) {
			const replays = typeof _replays == 'boolean' ? {} : _replays;
			integrations.push(
				Sentry.replayIntegration({ maskAllText: replays.maskAllText ?? false }),
			);
			options.replaysSessionSampleRate = replays.sampleRate ?? 0.1;
			options.replaysOnErrorSampleRate = replays.errorSampleRate ?? 1;
		}
		Sentry.init(options);
		_sentry = Sentry;
		document.addEventListener('boot', ({ detail }) => {
			const validatedId = detail.currentUser.id.replace(/[,;=| ]+/gu, '_');
			Sentry.setUser({ id: validatedId });
		});
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error('Failed to load application Sentry!');
		// eslint-disable-next-line no-console
		console.error(e);
	}
};
